import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-hot-toast";
import { STATUSES } from "../globals/misc/statuses";
import { API, APIAuthenticated } from "../globals/http";

const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
})();

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: storedUser,
    status: STATUSES.IDLE,
    token: localStorage.getItem("token") || "",
    error: null,
  },
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    setToken(state, action) {
      state.token = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    clearAuth(state) {
      state.user = null;
      state.token = "";
      state.status = STATUSES.IDLE;
      state.error = null;
    },
  },
});

export const { setUser, setStatus, setToken, setError, clearAuth } =
  authSlice.actions;
export default authSlice.reducer;

const persistSession = (token, user) => {
  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(user));
};

export function registerUser(data) {
  return async function registerUserThunk(dispatch) {
    dispatch(setStatus(STATUSES.LOADING));
    dispatch(setError(null));
    try {
      const response = await API.post("/auth/register", data);
      if (response.data?.success) {
        dispatch(setStatus(STATUSES.SUCCESS));
        toast.success(response.data.message || "Registered successfully");
        return true;
      }
      throw new Error(response.data?.message || "Registration failed");
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Registration failed";
      dispatch(setError(message));
      dispatch(setStatus(STATUSES.ERROR));
      toast.error(message);
      return false;
    }
  };
}

export function loginUser(data) {
  return async function loginUserThunk(dispatch) {
    dispatch(setStatus(STATUSES.LOADING));
    dispatch(setError(null));
    try {
      const response = await API.post("/auth/login", data);
      if (response.data?.success) {
        const { token, user } = response.data.data;
        persistSession(token, user);
        dispatch(setUser(user));
        dispatch(setToken(token));
        dispatch(setStatus(STATUSES.SUCCESS));
        toast.success(response.data.message || "Logged in successfully");
        return true;
      }
      throw new Error(response.data?.message || "Login failed");
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Login failed";
      dispatch(setError(message));
      dispatch(setStatus(STATUSES.ERROR));
      toast.error(message);
      return false;
    }
  };
}

export function fetchCurrentUser() {
  return async function fetchCurrentUserThunk(dispatch) {
    const token = localStorage.getItem("token");
    if (!token) return null;

    try {
      const response = await APIAuthenticated.get("/auth/me");
      if (response.data?.success) {
        const user = response.data.data;
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(setUser(user));
        dispatch(setToken(token));
        return user;
      }
      return null;
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch(clearAuth());
      return null;
    }
  };
}

export function logoutUser() {
  return function logoutUserThunk(dispatch) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    dispatch(clearAuth());
    toast.success("Logged out");
  };
}
